/**
 * 🏢 WORKSPACE CONTEXT — Isolamento Multi-Cliente
 *
 * Busca organizações do Supabase (user_organizations + organizations).
 * Admin/Consultor vê seletor pós-login.
 * Gerente/Operador é auto-dirigido à sua organização.
 *
 * Todas as chamadas de API devem usar useOrganizationId()
 * para respeitar o isolamento multi-cliente.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { Workspace, OrganizationOption, UserRole } from '../types/workspace';
import { getPermissionsForRole } from '../types/workspace';
import type { AuthMembership } from '../context/AuthContext';

interface WorkspaceContextType {
  workspace: Workspace | null;
  availableOrganizations: OrganizationOption[];
  isLoading: boolean;
  needsSelection: boolean;
  selectOrganization: (orgId: string) => void;
  switchOrganization: (orgId: string) => void;
  clearOrganization: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspace: null,
  availableOrganizations: [],
  isLoading: true,
  needsSelection: false,
  selectOrganization: () => {},
  switchOrganization: () => {},
  clearOrganization: () => {},
});

interface WorkspaceProviderProps {
  children: React.ReactNode;
  userId?: string;
  userRole?: UserRole;
  memberships?: AuthMembership[];
  onActiveOrganizationChange?: (organizationId: string, organizationName?: string) => void;
}

function buildOrganizationOptionsFromMemberships(memberships: AuthMembership[]): OrganizationOption[] {
  return memberships
    .map((membership) => ({
      id: membership.organizationId,
      name: membership.organizationName ?? membership.organizationId,
    }))
    .filter((org, index, array) => array.findIndex((candidate) => candidate.id === org.id) === index);
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({
  children,
  userId,
  userRole = 'operator',
  memberships = [],
  onActiveOrganizationChange,
}) => {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const resolveWorkspace = useCallback((org: OrganizationOption, membershipList: AuthMembership[]): Workspace => {
    const membership = membershipList.find((item) => item.organizationId === org.id);
    const effectiveRole = membership?.role ?? userRole;

    return {
      organizationId: org.id,
      organizationName: org.name,
      userRole: effectiveRole,
      permissions: getPermissionsForRole(effectiveRole),
    };
  }, [userRole]);

  const applyOrganizationSelection = useCallback((
    org: OrganizationOption,
    membershipList: AuthMembership[],
    persistSelection = true,
  ) => {
    setWorkspace(resolveWorkspace(org, membershipList));

    if (persistSelection) {
      try {
        localStorage.setItem('lean_office_active_org', org.id);
      } catch {
        /* ignore */
      }
    }

    onActiveOrganizationChange?.(org.id, org.name);
  }, [onActiveOrganizationChange, resolveWorkspace]);

  useEffect(() => {
    if (!userId) {
      setWorkspace(null);
      setOrganizations([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const autoSelectIfNeeded = (orgs: OrganizationOption[], membershipList: AuthMembership[]) => {
      if (orgs.length === 0) {
        setWorkspace(null);
        return;
      }

      try {
        const savedOrgId = localStorage.getItem('lean_office_active_org');
        if (savedOrgId) {
          const found = orgs.find((org) => org.id === savedOrgId);
          if (found) {
            applyOrganizationSelection(found, membershipList, false);
            return;
          }
        }
      } catch {
        /* ignore */
      }

      applyOrganizationSelection(orgs[0], membershipList, false);
    };

    const fetchOrganizations = async () => {
      setIsLoading(true);
      try {
        if (memberships.length > 0) {
          const orgOptions = buildOrganizationOptionsFromMemberships(memberships);
          if (!cancelled) {
            setOrganizations(orgOptions);
            autoSelectIfNeeded(orgOptions, memberships);
          }
          return;
        }

        const { data, error } = await supabase
          .from('user_organizations')
          .select('organization_id, role, organizations(id, name)')
          .eq('user_id', userId);

        if (error) {
          console.warn('[WorkspaceContext] Supabase query failed, using fallback:', error.message);

          const { data: orgs } = await supabase
            .from('organizations')
            .select('id, name')
            .limit(20);

          if (!cancelled && orgs && orgs.length > 0) {
            const orgOptions: OrganizationOption[] = orgs.map((org: any) => ({
              id: org.id,
              name: org.name,
            }));
            setOrganizations(orgOptions);
            autoSelectIfNeeded(orgOptions, []);
          } else if (!cancelled) {
            const devOrg: OrganizationOption = { id: 'dev-org', name: 'Minha Organização' };
            setOrganizations([devOrg]);
            autoSelectIfNeeded([devOrg], []);
          }
          return;
        }

        if (!cancelled && data) {
          const orgOptions: OrganizationOption[] = data
            .map((row: any) => {
              const org = row.organizations;
              if (!org) return null;
              return {
                id: org.id,
                name: org.name,
              };
            })
            .filter(Boolean) as OrganizationOption[];

          if (orgOptions.length === 0) {
            const devOrg: OrganizationOption = { id: 'dev-org', name: 'Minha Organização' };
            setOrganizations([devOrg]);
            autoSelectIfNeeded([devOrg], []);
          } else {
            setOrganizations(orgOptions);
            autoSelectIfNeeded(orgOptions, []);
          }
        }
      } catch (err) {
        console.warn('[WorkspaceContext] Error fetching organizations:', err);
        const devOrg: OrganizationOption = { id: 'dev-org', name: 'Minha Organização' };
        if (!cancelled) {
          setOrganizations([devOrg]);
          autoSelectIfNeeded([devOrg], []);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchOrganizations();
    return () => { cancelled = true; };
  }, [applyOrganizationSelection, memberships, userId]);

  const selectOrganization = useCallback((orgId: string) => {
    const org = organizations.find((item) => item.id === orgId);
    if (!org) return;

    applyOrganizationSelection(org, memberships, true);
  }, [applyOrganizationSelection, memberships, organizations]);

  const canSwitch = workspace?.permissions.canSwitchOrganization
    ?? getPermissionsForRole(userRole).canSwitchOrganization;

  const switchOrganization = useCallback((orgId: string) => {
    if (!canSwitch) return;
    selectOrganization(orgId);
  }, [canSwitch, selectOrganization]);

  const clearOrganization = useCallback(() => {
    setWorkspace(null);
    try {
      localStorage.removeItem('lean_office_active_org');
    } catch {
      /* ignore */
    }
  }, []);

  const needsSelection = !isLoading && !workspace && organizations.length > 0;

  return (
    <WorkspaceContext.Provider value={{
      workspace,
      availableOrganizations: organizations,
      isLoading,
      needsSelection,
      selectOrganization,
      switchOrganization,
      clearOrganization,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);

export const useOrganizationId = (): string => {
  const { workspace } = useWorkspace();
  return workspace?.organizationId ?? '';
};
